import { useEffect, useRef, useState } from "react";
import api from "../../../lib/api";
import { card, btn, btnGhost, label } from "./_genCommon";

/** Side-by-side viewer with synchronized zoom/pan + overlay toggle. */
export default function SideBySideCompare({ projectId, alts, onClose }) {
  const [leftId, setLeftId] = useState(alts[0]?.id);
  const [rightId, setRightId] = useState(alts[1]?.id);
  const [view, setView] = useState(null);
  const [views, setViews] = useState([]);
  const [floor, setFloor] = useState(0);
  const [side, setSide] = useState("north");
  const [svg, setSvg] = useState({});
  const [overlay, setOverlay] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ down: false, x: 0, y: 0 });
  const domain = alts[0]?.domain || "real_estate";

  useEffect(() => {
    api.get(`/projects/${projectId}/generative/views?domain=${domain}`)
        .then(r => {
          // SxS only supports SVG views (3D + tables don't make sense)
          const list = (r.data?.views || [])
              .filter(v => v.kind === "svg" && v.key !== "thumbnail");
          setViews(list);
          if (!view && list.length) setView(list[0].key);
        })
        .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain]);

  useEffect(() => {
    if (!view) return;
    [leftId, rightId].forEach(altId => {
      const cacheKey = `${altId}::${view}::${floor}::${side}`;
      if (!altId || svg[cacheKey]) return;
      const params = new URLSearchParams();
      if (view === "floor-plan") params.set("floor", floor);
      if (view === "elevation") params.set("side", side);
      api.get(`/projects/${projectId}/generative/alternatives/${altId}/view/${view}.svg`
                + (params.toString() ? `?${params.toString()}` : ""))
          .then(r => setSvg(prev => ({ ...prev, [cacheKey]: r.data?.svg })))
          .catch(() => {});
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, leftId, rightId, floor, side]);

  // Reset transform when view changes
  useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, [view]);

  const onWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.001;
    setZoom(z => Math.max(0.3, Math.min(5, z + delta)));
  };
  const onDown = (e) => {
    dragRef.current = { down: true, x: e.clientX, y: e.clientY };
  };
  const onMove = (e) => {
    if (!dragRef.current.down) return;
    setPan(p => ({ x: p.x + (e.clientX - dragRef.current.x),
                          y: p.y + (e.clientY - dragRef.current.y) }));
    dragRef.current.x = e.clientX;
    dragRef.current.y = e.clientY;
  };
  const onUp = () => { dragRef.current.down = false; };
  const onReset = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const left = alts.find(a => a.id === leftId);
  const right = alts.find(a => a.id === rightId);
  const leftSvg = svg[`${leftId}::${view}::${floor}::${side}`];
  const rightSvg = svg[`${rightId}::${view}::${floor}::${side}`];
  const totalFloors = Math.max(left?.areas?.floors || 1, right?.areas?.floors || 1);

  const transform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`;

  return (
    <div data-testid="gen-sxs" style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)",
        zIndex: 1000, display: "flex", flexDirection: "column",
        padding: 16,
    }}
        onMouseUp={onUp} onMouseLeave={onUp}>
      <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#F26B1F" }}>
          Side-by-side comparison
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11 }}>
          <span style={{ color: "var(--pf-muted)" }}>Zoom: <b>{zoom.toFixed(2)}×</b></span>
          <button data-testid="gen-sxs-reset" style={btnGhost} onClick={onReset}>
            Reset view
          </button>
          <label style={{ ...btnGhost, padding: "4px 10px",
                                background: overlay ? "rgba(242,107,31,0.20)" : "transparent",
                                color: overlay ? "#F26B1F" : "var(--pf-text)",
                                cursor: "pointer", fontSize: 11 }}>
            <input type="checkbox" data-testid="gen-sxs-overlay"
                checked={overlay} onChange={(e) => setOverlay(e.target.checked)}
                style={{ marginRight: 6, accentColor: "#F26B1F" }} />
            Overlay mode
          </label>
          <button data-testid="gen-sxs-close" style={btnGhost} onClick={onClose}>
            ✕ Close
          </button>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        {views.map(v => (
          <button key={v.key} onClick={() => setView(v.key)}
              data-testid={`gen-sxs-tab-${v.key}`}
              style={{ ...btnGhost,
                          borderColor: view === v.key ? "#F26B1F" : "var(--pf-border)",
                          color: view === v.key ? "#F26B1F" : "var(--pf-text)",
                          padding: "5px 10px", fontSize: 11 }}>
            {v.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {view === "floor-plan" && (
          <select value={floor} onChange={(e) => setFloor(parseInt(e.target.value))}
              style={{ ...btnGhost, fontSize: 11, padding: "4px 8px" }}>
            {Array.from({ length: totalFloors }, (_, i) => (
              <option key={i} value={i}>Floor {i === 0 ? "GF" : i}</option>
            ))}
          </select>
        )}
        {view === "elevation" && (
          <select value={side} onChange={(e) => setSide(e.target.value)}
              style={{ ...btnGhost, fontSize: 11, padding: "4px 8px" }}>
            {["north", "south", "east", "west"].map(s =>
                <option key={s} value={s}>{s.toUpperCase()}</option>)}
          </select>
        )}
        <span style={{ fontSize: 10, color: "var(--pf-muted)",
                              alignSelf: "center", marginLeft: 8 }}>
          Drag to pan · scroll to zoom · synced
        </span>
      </div>

      {/* OVERLAY MODE: stack the two SVGs with right at 50% opacity */}
      {overlay ? (
        <div style={{ flex: 1, ...card, padding: 0, position: "relative",
                            overflow: "hidden", background: "#0E1116" }}
              onWheel={onWheel} onMouseDown={onDown} onMouseMove={onMove}>
          <div style={{ ...card, position: "absolute", top: 8, left: 8, zIndex: 5,
                              padding: 6, fontSize: 10 }}>
            <select value={leftId || ""}
                onChange={(e) => setLeftId(e.target.value)}
                data-testid="gen-sxs-pick-0"
                style={{ ...btnGhost, fontSize: 11, padding: 4 }}>
              {alts.map(a => (
                <option key={a.id} value={a.id}>
                  {a.id} · {a.strategy_name || ""}
                </option>
              ))}
            </select>
            <span style={{ marginLeft: 6, color: "#F26B1F" }}>(base)</span>
          </div>
          <div style={{ ...card, position: "absolute", top: 8, right: 8, zIndex: 5,
                              padding: 6, fontSize: 10 }}>
            <select value={rightId || ""}
                onChange={(e) => setRightId(e.target.value)}
                data-testid="gen-sxs-pick-1"
                style={{ ...btnGhost, fontSize: 11, padding: 4 }}>
              {alts.map(a => (
                <option key={a.id} value={a.id}>
                  {a.id} · {a.strategy_name || ""}
                </option>
              ))}
            </select>
            <span style={{ marginLeft: 6, color: "#22c55e" }}>(overlay 50%)</span>
          </div>
          <div style={{ position: "absolute", inset: 0,
                              transform, transformOrigin: "0 0",
                              cursor: "grab" }}>
            {leftSvg && <div style={{ position: "absolute", inset: 0,
                                                  filter: "hue-rotate(0deg) saturate(0.7)" }}
                                      dangerouslySetInnerHTML={{ __html: leftSvg }} />}
            {rightSvg && <div style={{ position: "absolute", inset: 0,
                                                  opacity: 0.55,
                                                  mixBlendMode: "screen",
                                                  filter: "hue-rotate(140deg) saturate(1.3)" }}
                                        dangerouslySetInnerHTML={{ __html: rightSvg }} />}
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, display: "grid",
                            gridTemplateColumns: "1fr 1fr", gap: 12, minHeight: 0 }}>
          {[{a: left, id: leftId, set: setLeftId, svg: leftSvg, idx: 0},
              {a: right, id: rightId, set: setRightId, svg: rightSvg, idx: 1}].map((s, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column",
                                              minHeight: 0 }}>
              <div style={{ ...card, padding: 8 }}>
                <select value={s.id || ""}
                    onChange={(e) => s.set(e.target.value)}
                    data-testid={`gen-sxs-pick-${i}`}
                    style={{ ...btnGhost, fontSize: 11, padding: 6, width: "100%" }}>
                  {alts.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.id} · {a.strategy_name || ""} · {a.cost?.capex_INR_cr} cr
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ ...card, marginTop: 6, padding: 4, flex: 1,
                                  overflow: "hidden", background: "#0E1116",
                                  cursor: dragRef.current.down ? "grabbing" : "grab" }}
                    onWheel={onWheel} onMouseDown={onDown} onMouseMove={onMove}>
                {s.svg
                    ? <div style={{ transform, transformOrigin: "0 0" }}
                                  dangerouslySetInnerHTML={{ __html: s.svg }} />
                    : <div style={{ padding: 80, textAlign: "center",
                                          color: "var(--pf-muted)", fontSize: 11 }}>
                        Loading…
                      </div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
